import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, ChevronDown, X } from "lucide-react";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import LogoMark from "@/components/ui/LogoMark";
import { usePhoneNumber } from "@/hooks/usePhoneNumber";
import { TRACK_LIST, getTrack, setPreferredTrack } from "@/lib/tracks";
import { cn } from "@/lib/utils";

const Logo = ({ onClick }) => (
    <Link
        to="/"
        onClick={onClick}
        className="flex items-center gap-2.5 group"
        data-testid="header-logo"
    >
        <LogoMark
            className="h-9 w-auto shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5"
            title="Reformity Inc."
        />
        <span className="font-sans font-bold text-base sm:text-lg md:text-xl tracking-tight leading-none text-white hidden sm:block">
            Reformity Inc.
        </span>
    </Link>
);

// Persistent track switcher (Residential | Commercial | Industrial).
// Crawlable internal links + sets the preferredTrack cookie on click.
const TrackSwitcher = ({ activeKey, onNavigate, className }) => (
    <nav
        className={cn("flex items-center gap-1", className)}
        aria-label="Audience track"
        data-testid="track-switcher"
    >
        {TRACK_LIST.map((t) => {
            const active = t.key === activeKey;
            return (
                <Link
                    key={t.key}
                    to={t.home}
                    onClick={() => {
                        setPreferredTrack(t.key);
                        onNavigate?.();
                    }}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                        "px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors duration-200",
                        active
                            ? "bg-brand-cyan text-brand-dark"
                            : "text-white/70 hover:text-brand-cyan",
                    )}
                    data-testid={`track-switch-${t.key}`}
                >
                    {t.label}
                </Link>
            );
        })}
    </nav>
);

const DesktopServicesDropdown = ({ track }) => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const isActive = location.pathname.startsWith(track.servicesBase);

    return (
        <div
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            data-testid="services-dropdown-wrapper"
        >
            <Link
                to={track.servicesIndex}
                className={cn(
                    "inline-flex items-center gap-1 px-1 py-2 text-sm font-semibold uppercase tracking-wider transition-colors duration-200",
                    isActive
                        ? "text-brand-cyan"
                        : "text-white hover:text-brand-cyan",
                )}
                data-testid="services-dropdown-trigger"
                aria-haspopup="true"
                aria-expanded={open}
            >
                Services
                <ChevronDown
                    className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        open && "rotate-180",
                    )}
                    strokeWidth={2.25}
                />
            </Link>

            {/* Invisible hover bridge to prevent flicker */}
            <div className="absolute left-0 right-0 top-full h-3" aria-hidden="true" />

            <div
                className={cn(
                    "absolute left-0 top-full mt-3 w-72 origin-top-left bg-white shadow-xl border border-black/5 transition-all duration-200",
                    open
                        ? "pointer-events-auto opacity-100 translate-y-0"
                        : "pointer-events-none opacity-0 -translate-y-1",
                )}
                role="menu"
                data-testid="services-dropdown-menu"
            >
                <ul className="py-2">
                    <li>
                        <Link
                            to={track.servicesIndex}
                            className="flex items-center justify-between px-5 py-3 text-sm font-semibold text-brand-dark hover:bg-brand-offwhite hover:text-brand-cyan transition-colors"
                            data-testid="services-dropdown-all"
                        >
                            All Services
                            <span className="text-xs font-normal uppercase tracking-wider text-brand-gray">
                                Overview
                            </span>
                        </Link>
                    </li>
                    <li className="my-1 h-px bg-black/5" aria-hidden="true" />
                    {track.services.map((s) => (
                        <li key={s.path}>
                            <Link
                                to={s.path}
                                className="block px-5 py-3 text-sm text-brand-dark hover:bg-brand-offwhite hover:text-brand-cyan transition-colors"
                                data-testid={`services-dropdown-item-${s.path.split("/").pop()}`}
                            >
                                {s.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const desktopLinkClass = ({ isActive }) =>
    cn(
        "px-1 py-2 text-sm font-semibold uppercase tracking-wider transition-colors duration-200",
        isActive ? "text-brand-cyan" : "text-white hover:text-brand-cyan",
    );

const MobileDrawer = ({ open, onClose, track }) => {
    const [servicesOpen, setServicesOpen] = useState(false);
    const { phone, phoneHref } = usePhoneNumber();

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <div
                onClick={onClose}
                className={cn(
                    "fixed inset-0 z-40 bg-black/60 transition-opacity duration-300 md:hidden",
                    open ? "opacity-100" : "pointer-events-none opacity-0",
                )}
                aria-hidden="true"
            />
            <aside
                className={cn(
                    "fixed inset-y-0 right-0 z-50 w-[88%] max-w-sm bg-brand-dark text-white shadow-2xl transition-transform duration-300 md:hidden overflow-y-auto",
                    open ? "translate-x-0" : "translate-x-full",
                )}
                role="dialog"
                aria-label="Mobile navigation"
                data-testid="mobile-drawer"
            >
                <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                    <span className="font-display text-2xl tracking-wider">MENU</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 -mr-2 hover:text-brand-cyan transition-colors"
                        aria-label="Close menu"
                        data-testid="mobile-drawer-close"
                    >
                        <X className="h-6 w-6" strokeWidth={2.25} />
                    </button>
                </div>

                {/* Track switcher at the top of the drawer */}
                <div className="px-5 py-4 border-b border-white/10">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">
                        Choose your track
                    </p>
                    <TrackSwitcher
                        activeKey={track.key}
                        onNavigate={onClose}
                        className="gap-1"
                    />
                </div>

                <nav className="flex flex-col px-1 py-4">
                    <button
                        type="button"
                        onClick={() => setServicesOpen((v) => !v)}
                        className="flex items-center justify-between px-5 py-3 text-base font-semibold uppercase tracking-wider hover:text-brand-cyan transition-colors"
                        data-testid="mobile-services-toggle"
                        aria-expanded={servicesOpen}
                    >
                        Services
                        <ChevronDown
                            className={cn(
                                "h-5 w-5 transition-transform duration-200",
                                servicesOpen && "rotate-180",
                            )}
                            strokeWidth={2.25}
                        />
                    </button>
                    <div
                        className={cn(
                            "overflow-hidden transition-all duration-300",
                            servicesOpen ? "max-h-[32rem]" : "max-h-0",
                        )}
                    >
                        <ul className="border-l-2 border-brand-coral ml-5 my-1">
                            <li>
                                <Link
                                    to={track.servicesIndex}
                                    onClick={onClose}
                                    className="block px-4 py-2.5 text-sm text-white/80 hover:text-brand-cyan transition-colors"
                                >
                                    All Services
                                </Link>
                            </li>
                            {track.services.map((s) => (
                                <li key={s.path}>
                                    <Link
                                        to={s.path}
                                        onClick={onClose}
                                        className="block px-4 py-2.5 text-sm text-white/80 hover:text-brand-cyan transition-colors"
                                        data-testid={`mobile-services-${s.path.split("/").pop()}`}
                                    >
                                        {s.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {track.navLinks.map((link) => (
                        <NavLink
                            key={link.path}
                            to={link.path}
                            onClick={onClose}
                            className={({ isActive }) =>
                                cn(
                                    "px-5 py-3 text-base font-semibold uppercase tracking-wider transition-colors",
                                    isActive
                                        ? "text-brand-cyan"
                                        : "hover:text-brand-cyan",
                                )
                            }
                            data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="mt-2 border-t border-white/10 px-5 py-5 space-y-3">
                    <a
                        href={phoneHref}
                        className="flex items-center gap-3 text-base text-white hover:text-brand-cyan transition-colors"
                        data-testid="mobile-phone-link"
                    >
                        <Phone className="h-5 w-5 text-brand-coral" strokeWidth={2.25} />
                        <span className="font-semibold">{phone}</span>
                    </a>
                    <BrandButton
                        asChild
                        variant="primary"
                        size="lg"
                        className="w-full"
                    >
                        <Link
                            to={track.contact}
                            onClick={onClose}
                            data-testid="mobile-cta-quote"
                        >
                            Get a Free Quote
                        </Link>
                    </BrandButton>
                </div>
            </aside>
        </>
    );
};

const Header = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { phone, phoneHref } = usePhoneNumber();
    const location = useLocation();
    const track = getTrack(location.pathname);

    // Close drawer on route change
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="sticky top-0 z-30 w-full bg-brand-dark text-white shadow-md"
            data-testid="site-header"
        >
            {/* Slim persistent track-switcher strip (desktop) */}
            <div className="hidden md:block border-b border-white/10 bg-black/20">
                <Container className="flex h-9 items-center justify-end">
                    <TrackSwitcher activeKey={track.key} />
                </Container>
            </div>

            <Container className="flex h-16 md:h-20 items-center justify-between gap-4">
                <Logo />

                <nav
                    className="hidden md:flex items-center gap-7 lg:gap-9"
                    aria-label="Primary"
                >
                    <DesktopServicesDropdown track={track} />
                    {track.navLinks.map((link) => (
                        <NavLink
                            key={link.path}
                            to={link.path}
                            className={desktopLinkClass}
                            data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3 md:gap-5">
                    <a
                        href={phoneHref}
                        className="hidden lg:flex items-center gap-2 text-sm font-semibold text-white hover:text-brand-cyan transition-colors"
                        data-testid="header-phone-link"
                    >
                        <Phone className="h-4 w-4 text-brand-coral" strokeWidth={2.25} />
                        {phone}
                    </a>

                    <BrandButton
                        asChild
                        variant="primary"
                        size="md"
                        className="hidden md:inline-flex"
                    >
                        <Link to={track.contact} data-testid="header-cta-quote">
                            Get a Free Quote
                        </Link>
                    </BrandButton>

                    <button
                        type="button"
                        onClick={() => setDrawerOpen(true)}
                        className="md:hidden p-2 -mr-2 text-white hover:text-brand-cyan transition-colors"
                        aria-label="Open menu"
                        data-testid="mobile-menu-toggle"
                    >
                        <Menu className="h-6 w-6" strokeWidth={2.25} />
                    </button>
                </div>
            </Container>

            <MobileDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                track={track}
            />
        </header>
    );
};

export default Header;
